import React, { useState } from 'react';
import { Asset, CreateAssetData } from '../../services/assetAPI';
import { X, Save, Server } from 'lucide-react';

interface AssetFormProps {
  asset: Asset | null;
  onSave: (data: CreateAssetData) => Promise<void>;
  onClose: () => void;
}

const AssetForm: React.FC<AssetFormProps> = ({ asset, onSave, onClose }) => {
  const [formData, setFormData] = useState<CreateAssetData>({
    asset_name: asset?.asset_name || '',
    asset_type: asset?.asset_type || 'infrastructure',
    classification: asset?.classification || 'important',
    description: asset?.description || '',
    owner_name: asset?.owner_name || '',
    owner_email: asset?.owner_email || '',
    location: asset?.location || '',
    data_stored: asset?.data_stored || '',
    access_controls: asset?.access_controls || '',
    encryption_status: asset?.encryption_status || false,
    encryption_details: asset?.encryption_details || '',
    encryption_at_rest: asset?.encryption_at_rest || '',
    encryption_in_transit: asset?.encryption_in_transit || '',
    backup_status: asset?.backup_status || false,
    backup_frequency: asset?.backup_frequency || '',
    backup_retention: asset?.backup_retention || '',
    backup_details: asset?.backup_details || '',
    review_frequency_months: asset?.review_frequency_months || 6,
    vendor_name: asset?.vendor_name || '',
    special_notes: asset?.special_notes || '',
    tags: asset?.tags || []
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save asset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof CreateAssetData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Server size={24} className="text-white" />
              <h2 className="text-xl font-semibold text-white">
                {asset ? 'Edit Asset' : 'Create New Asset'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    value={formData.asset_name}
                    onChange={(e) => handleChange('asset_name', e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Railway PostgreSQL Database"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Type *
                  </label>
                  <select
                    value={formData.asset_type}
                    onChange={(e) => handleChange('asset_type', e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="infrastructure">Infrastructure</option>
                    <option value="application">Application</option>
                    <option value="service">Service</option>
                    <option value="code_repository">Code Repository</option>
                    <option value="data">Data</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Classification *
                  </label>
                  <select
                    value={formData.classification}
                    onChange={(e) => handleChange('classification', e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="critical">Critical</option>
                    <option value="important">Important</option>
                    <option value="low_risk">Low Risk</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Brief description of the asset..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g., AWS US-East-1, Railway PaaS"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor/Provider
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_name}
                    onChange={(e) => handleChange('vendor_name', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g., Railway, AWS, Anthropic"
                  />
                </div>
              </div>
            </div>

            {/* Ownership */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Ownership
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner Name
                  </label>
                  <input
                    type="text"
                    value={formData.owner_name}
                    onChange={(e) => handleChange('owner_name', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g., Ryan Nokes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner Email
                  </label>
                  <input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => handleChange('owner_email', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="owner@company.com"
                  />
                </div>
              </div>
            </div>

            {/* Data & Access */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Data & Access
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Stored
                  </label>
                  <textarea
                    value={formData.data_stored}
                    onChange={(e) => handleChange('data_stored', e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Description of data stored in this asset..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Controls
                  </label>
                  <textarea
                    value={formData.access_controls}
                    onChange={(e) => handleChange('access_controls', e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Description of access controls..."
                  />
                </div>
              </div>
            </div>

            {/* Security */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Security
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.encryption_status}
                      onChange={(e) => handleChange('encryption_status', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Encryption Enabled</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.backup_status}
                      onChange={(e) => handleChange('backup_status', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Backup Enabled</span>
                  </label>
                </div>

                {formData.encryption_status && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Encryption at Rest
                      </label>
                      <input
                        type="text"
                        value={formData.encryption_at_rest}
                        onChange={(e) => handleChange('encryption_at_rest', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., AES-256"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Encryption in Transit
                      </label>
                      <input
                        type="text"
                        value={formData.encryption_in_transit}
                        onChange={(e) => handleChange('encryption_in_transit', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., TLS 1.3"
                      />
                    </div>
                  </div>
                )}

                {formData.backup_status && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Backup Frequency
                      </label>
                      <input
                        type="text"
                        value={formData.backup_frequency}
                        onChange={(e) => handleChange('backup_frequency', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., Daily automated"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Backup Retention
                      </label>
                      <input
                        type="text"
                        value={formData.backup_retention}
                        onChange={(e) => handleChange('backup_retention', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., 30 days"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Review Settings */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Review Settings
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Review Frequency (months)
                </label>
                <select
                  value={formData.review_frequency_months}
                  onChange={(e) => handleChange('review_frequency_months', parseInt(e.target.value))}
                  className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value={3}>Quarterly (3 months)</option>
                  <option value={6}>Semi-annually (6 months)</option>
                  <option value={12}>Annually (12 months)</option>
                </select>
              </div>
            </div>

            {/* Special Notes */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Additional Information
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Notes
                </label>
                <textarea
                  value={formData.special_notes}
                  onChange={(e) => handleChange('special_notes', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Any special notes or considerations for this asset..."
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !formData.asset_name}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Save size={16} />
            {isSaving ? 'Saving...' : asset ? 'Update Asset' : 'Create Asset'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetForm;
